import { PriorAuthorizationListItem } from './types'
import { Box, Grid, Typography, List, ListItem, IconButton } from '@mui/material'
import ContentCopy from '@mui/icons-material/ContentCopy'
import Check from '@mui/icons-material/Check'
import { useMutation } from '@tanstack/react-query'
import { asDate, fullName } from './helpers'

interface LocalProps {
  priorAuth: PriorAuthorizationListItem
}

const PriorAuthorizationItem = (props: LocalProps) => {
  const { priorAuth } = props

  const { mutate: copyToClipboard, isSuccess } = useMutation({
    mutationFn: (text: string) => navigator.clipboard.writeText(text),
  })
  // Reflect health PA has originating and referred_to providers
  const originatingProvider = priorAuth.providers.filter(
    provider => provider.provider_type === 'originating',
  )
  const referredToProvider = priorAuth.providers.filter(
    provider => provider.provider_type === 'referred_to',
  )
  // The providers from flume PA does not have type,
  // so we are filtering out the originating and referred_to providers and displaying them as just provider
  // This is for Backward compatibility to support both flume PA
  const providers = priorAuth.providers.filter(
    provider =>
      provider.provider_type !== 'referred_to' && provider.provider_type !== 'originating',
  )

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Determination
          </Typography>
          {priorAuth.determination ? (
            <Typography variant="body2">
              {priorAuth.determination} on {asDate(priorAuth.vendor_update_date)}
            </Typography>
          ) : null}
        </Grid>
        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Service Quantity
          </Typography>
          {priorAuth.service_quantity?.value == null ? (
            <Typography variant="body2">(none)</Typography>
          ) : (
            <Typography variant="body2">{`${priorAuth.service_quantity.value} ${priorAuth.service_quantity.unit}`}</Typography>
          )}
        </Grid>

        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Effective Date
          </Typography>
          <Typography variant="body2">{asDate(priorAuth.effective_from)}</Typography>
        </Grid>

        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Expiration Date
          </Typography>
          <Typography variant="body2">{asDate(priorAuth.effective_through)}</Typography>
        </Grid>

        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Originator System Id
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">{priorAuth.originator_system_id}</Typography>
            {priorAuth.originator_system_id != null ? (
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  // This cast is safe because we check for null above
                  onClick={() => copyToClipboard(priorAuth.originator_system_id as string)}
                  title="Copy to clipboard"
                  sx={{ visibility: isSuccess ? 'hidden' : 'visible' }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
                {isSuccess ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Check fontSize="small" color="success" />
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      copied
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Grid>

        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Status
          </Typography>
          {!priorAuth.status ? (
            <Typography variant="body2">-</Typography>
          ) : (
            <Typography variant="body2">{`${priorAuth.status}`}</Typography>
          )}
        </Grid>
        {originatingProvider.map((provider, index) => (
          <Grid key={`${index} ${provider.npi}`} size={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {index > 0 ? `Originating Provider (${index + 1})` : 'Originating Provider'}
            </Typography>
            {fullName(provider) ? (
              <Typography variant="body2">{fullName(provider)}</Typography>
            ) : null}
            {provider.street_address ? (
              <Typography variant="body2">{provider.street_address}</Typography>
            ) : null}
            {provider?.city || provider.state?.abbreviation || provider?.zip_code ? (
              <Typography variant="body2">
                {provider?.city}, {provider.state?.abbreviation} {provider?.zip_code}
              </Typography>
            ) : null}
            {provider.npi?.trim() ? (
              <Typography variant="body2">NPI: {provider.npi}</Typography>
            ) : null}
          </Grid>
        ))}
        {referredToProvider.map((provider, index) => (
          <Grid key={`${index} ${provider.npi}`} size={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {index > 0 ? `Referred To Provider (${index + 1})` : 'Referred To Provider'}
            </Typography>
            {fullName(provider) ? (
              <Typography variant="body2">{fullName(provider)}</Typography>
            ) : null}
            {provider.street_address ? (
              <Typography variant="body2">{provider.street_address}</Typography>
            ) : null}
            {provider?.city || provider.state?.abbreviation || provider?.zip_code ? (
              <Typography variant="body2">
                {provider?.city}, {provider.state?.abbreviation} {provider?.zip_code}
              </Typography>
            ) : null}
            {provider.npi?.trim() ? (
              <Typography variant="body2">NPI: {provider.npi}</Typography>
            ) : null}
          </Grid>
        ))}

        {providers.map((provider, index) => (
          <Grid key={`${index} ${provider.npi}`} size={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {index > 0 ? `Provider (${index + 1})` : 'Provider'}
            </Typography>
            {fullName(provider) ? (
              <Typography variant="body2">{fullName(provider)}</Typography>
            ) : null}
            {provider.street_address ? (
              <Typography variant="body2">{provider.street_address}</Typography>
            ) : null}
            {provider?.city || provider.state?.abbreviation || provider?.zip_code ? (
              <Typography variant="body2">
                {provider?.city}, {provider.state?.abbreviation} {provider?.zip_code}
              </Typography>
            ) : null}
            {provider.npi?.trim() ? (
              <Typography variant="body2">NPI: {provider.npi}</Typography>
            ) : null}
          </Grid>
        ))}

        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Updated Date
          </Typography>
          <Typography variant="body2">{asDate(priorAuth?.updated_at)}</Typography>
        </Grid>

        {priorAuth.procedure_codes.length === 0 ? null : (
          <Grid size={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Procedure Codes
            </Typography>
            <List dense disablePadding>
              {priorAuth.procedure_codes.map((procedureCode, index) => (
                <ListItem key={index} disablePadding>
                  <Typography variant="body2">
                    {procedureCode.system}-{procedureCode.version}, {procedureCode.code},{' '}
                    {procedureCode.content_object?.description}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Grid>
        )}

        {priorAuth.diagnosis_codes.length === 0 ? null : (
          <Grid size={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Diagnosis Codes
            </Typography>
            <List dense disablePadding>
              {priorAuth.diagnosis_codes.map((diagnosisCode, index) => (
                <ListItem key={index} disablePadding>
                  <Typography variant="body2">
                    {diagnosisCode.system}-{diagnosisCode.version}, {diagnosisCode.code}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export default PriorAuthorizationItem
